import { Injectable } from '@angular/core';
import { DateRange } from '@ot/shared/model';
import { LocalDate, ZoneId } from 'js-joda';
import { combineLatest, Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { IftaConstantsService } from '../constants';
import { TaxAccount, Vehicle } from '../models';
import { TaxAccountService } from '../tax-account/tax-account.service';
import { TimeTransform, VehicleTransform } from '../transformations';
import { UserService } from '../user';
import { VehicleService } from '../vehicle';

@Injectable({
  providedIn: 'root'
})
export class FiltersService {
  constructor(
    private userService: UserService,
    private constantsService: IftaConstantsService,
    private vehicleService: VehicleService,
    private taxAccountService: TaxAccountService
  ) {}

  getBusinessAccountId(): string {
    return this.userService.getBusinessAccount();
  }

  getDefaultDateRange(): Observable<DateRange> {
    return this.constantsService.getCurrentQuarter().pipe(
      mergeMap(quarter =>
        this.constantsService.getDatesforQuarter(quarter).pipe(
          map(dates => {
            const startDate = TimeTransform.split(dates[0]).date;
            const endDate = TimeTransform.parse(this.getTodaysDate()).date;
            return {
              startDate,
              endDate
            };
          })
        )
      )
    );
  }

  getTaxAccounts(): Observable<TaxAccount[]> {
    const baid = this.getBusinessAccountId();
    return this.taxAccountService
      .getTaxAccounts(
        {
          pageSize: '1000',
          pageToken: '1'
        },
        baid
      )
      .pipe(map(response => response.items));
  }

  getVehicles(taxAccountId: string): Observable<Vehicle[]> {
    const baid = this.getBusinessAccountId();
    switch (taxAccountId || '') {
      case '':
        return this.getAllVehicles();
      case 'unassigned':
        return this.getUnassignedVehicles(baid);
      default:
        return this.getVehicleIdsForTaxAccount(baid, taxAccountId);
    }
  }

  getAllVehicles(): Observable<Vehicle[]> {
    const baid = this.getBusinessAccountId();
    return this.vehicleService
      .getAllVehicles(baid)
      .pipe(map(vehicles => vehicles.map(VehicleTransform.fromAPI)));
  }

  getUnassignedVehicles(businessAccountId: string): Observable<Vehicle[]> {
    const allVehicles$ = this.getAllVehicles();
    const unassignedVehicleIds$ = this.taxAccountService.getUnassignedVehicleIds(
      businessAccountId
    );
    return combineLatest(allVehicles$, unassignedVehicleIds$).pipe(
      map(([allVehicles, unassignedVehicleIds]) =>
        allVehicles.filter(vehicle => unassignedVehicleIds.includes(vehicle.id))
      )
    );
  }

  getVehicleIdsForTaxAccount(
    businessAccountId: string,
    taxAccountId: string
  ): Observable<Vehicle[]> {
    const allVehicles$ = this.getAllVehicles();
    const assignedVehicleIds$ = this.taxAccountService.getVehicleIdsForTaxAccount(
      businessAccountId,
      taxAccountId
    );
    return combineLatest(allVehicles$, assignedVehicleIds$).pipe(
      map(([allVehicles, assignedVehicleIds]) =>
        allVehicles.filter(vehicle => assignedVehicleIds.includes(vehicle.id))
      )
    );
  }

  getTodaysDate(): string {
    return this.getDateAtEndOfDay(LocalDate.now());
  }

  getDefaultStartDate(): string {
    return LocalDate.now()
      .minusYears(1)
      .plusDays(1)
      .toString();
  }

  getDefaultEndDate(): string {
    return LocalDate.now().toString();
  }

  parseStartDate(date: string): string {
    if (!date) {
      return null;
    }
    return LocalDate.parse(date)
      .atStartOfDayWithZone(ZoneId.UTC)
      .toInstant()
      .toString();
  }

  parseEndDate(date: string): string {
    if (!date) {
      return null;
    }
    return this.getDateAtEndOfDay(LocalDate.parse(date));
  }

  getDateAtEndOfDay(date: LocalDate): string {
    return date
      .atStartOfDayWithZone(ZoneId.UTC)
      .plusDays(1)
      .minusSeconds(1)
      .toInstant()
      .toString();
  }
}
